import { integrationApi } from "@repo/api";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useIntl } from "react-intl";
import { getCronExpression } from "../../utils/schedule";

const DEFAULT_INTEGRATION_TYPE = "Agentforce";

interface UseCreateAgentforceIntegrationProps {
  onSuccess: (
    message: string,
    isDraft?: boolean,
    integrationType?: string,
  ) => void;
  onError: (message: string) => void;
}

export function useCreateAgentforceIntegration({
  onSuccess,
  onError,
}: UseCreateAgentforceIntegrationProps) {
  const intl = useIntl();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      isEnabled,
      data,
    }: {
      isEnabled: boolean;
      data: Record<string, unknown>;
    }) => {
      const payload = {
        displayName: (data.name as string) || "",
        isEnabled,
        integrationType:
          (data.integrationType as string) || DEFAULT_INTEGRATION_TYPE,
        credentials: {
          type: "OAUTH2",
          data: {
            clientId: (data.clientId as string) || "",
            clientSecret: (data.clientSecret as string) || "",
            domain: (data.domain as string) || "",
          },
        },
        metadata: {
          accountId: (data.organizationId as string) || "",
          description: (data.description as string) || "",
        },
        features: (
          (data.featureNames as string[]) || ["POSTURE_FOR_AGENTFORCE"]
        ).map((featureName) => ({
          featureName,
          schedules: [
            {
              scheduleName: "agentforce-schedule",
              cronExpression: getCronExpression(
                (data.frequency as string) || "24hours",
                data.importTime as Date | null,
              ),
            },
          ],
        })),
      };
      const type = (data.integrationType as string) || DEFAULT_INTEGRATION_TYPE;
      return integrationApi.createIntegration(type, payload);
    },
    onMutate: async ({ isEnabled, data }) => {
      await queryClient.cancelQueries({ queryKey: ["integrations"] });
      const previousIntegrations = queryClient.getQueryData(["integrations"]);

      queryClient.setQueryData(["integrations"], (old: unknown) => {
        const oldData = old as
          | { integrations?: Array<Record<string, unknown>> }
          | undefined;
        const newIntegration = {
          id: Date.now(),
          displayName: (data.name as string) || "",
          description: data.description as string,
          integrationType:
            (data.integrationType as string) || DEFAULT_INTEGRATION_TYPE,
          connectionStatus: "PENDING",
          isEnabled,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };
        return {
          ...oldData,
          integrations: [newIntegration, ...(oldData?.integrations || [])],
        };
      });

      return { previousIntegrations };
    },
    onError: (_err, _variables, context) => {
      if (context?.previousIntegrations) {
        queryClient.setQueryData(
          ["integrations"],
          context.previousIntegrations,
        );
      }
      onError(
        intl.formatMessage({
          id: "l8gxpc",
          defaultMessage: "Failed to create integration",
          description: "Error message for integration creation failure",
        }),
      );
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["integrations"] });
      const isDraft = !variables.isEnabled;
      if (isDraft) {
        onSuccess(
          intl.formatMessage(
            {
              id: "c2bz4O",
              defaultMessage:
                "You can resume the setup of {integrationType} at any time.",
              description: "Draft save success message",
            },
            {
              integrationType: variables.data.name as string,
            },
          ),
          true,
          variables.data.name as string,
        );
      } else {
        onSuccess(
          intl.formatMessage({
            id: "gRl5vB",
            defaultMessage: "Integration created successfully",
            description: "Success message for integration creation",
          }),
          false,
        );
      }
    },
  });
}
